// The relay client — everything <bb-widget> needs to talk to the BerryBot
// relay, in one module: a configured `fetch` (auth + storefront-pinning +
// 401-retry + an `onCall` observability hook), the SSE stream parser, the
// typed endpoint wrappers, and `ApiError` / `apiErrorMessage`. Paths mirror
// the relay surface in `ai-ordering-relay-api/openapi.json` — keep them in sync with it.

import type {
  CartAddRequest,
  CartRemoveRequest,
  ConversationRequest,
  ConversationResponse,
  ConversationState,
  AddressAutocompleteResponse,
  ContactRequest,
  ContactResponse,
  PlaceDetailsResponse,
  FulfillmentRequest,
  FulfillmentResponse,
  RandomAddressResponse,
  SseEvent,
  SseEventType,
  UtteranceRequest,
  UtteranceResponse,
} from './types'

// ── Configured fetch ──────────────────────────────────────────────────
//
// Builds a callable with the same signature as `fetch` that:
//   1. injects auth/context headers (X-V2-Token, X-Account-Id) + any extra
//      headers the host page wants;
//   2. appends `storefront=<id>` to the query string.
// `makeApi` (below) consumes one of these.

export interface BridgeFetchOptions {
  apiBase: string
  storefrontId: string
  // X-V2-Token (BerryCore JWT).
  getToken: () => Promise<string> | string
  getAccountId?: () => Promise<string | null> | string | null
  // Hook for refresh-on-401. If the response is 401 and `refreshToken`
  // is provided, it will be called once and the request retried.
  refreshToken?: () => Promise<string>
  // Observability hook: invoked once per call with the final outcome (after the
  // 401 retry, if any). `status` is 0 when `fetch` itself threw. `requestBody`
  // is the JSON string sent (if any); `responseBody` is the response text,
  // truncated, captured via a cheap `Response.clone()` — skipped for SSE.
  onCall?: (info: {
    method: string
    url: string
    status: number
    ms: number
    ok: boolean
    requestBody?: string
    responseBody?: string
  }) => void
}

const MAX_LOGGED_BODY = 100_000

export type BridgeFetch = (
  path: string,
  init?: RequestInit,
) => Promise<Response>

export function makeBridgeFetch(opts: BridgeFetchOptions): BridgeFetch {
  const buildUrl = (path: string): string => {
    // apiBase may be absolute (https://…) or origin-relative (/foo) and may
    // carry query params (e.g. /relay?env=test). Parse it properly so appending
    // a path segment doesn't corrupt the query string.
    const origin =
      typeof globalThis !== 'undefined' &&
      (globalThis as { location?: Location }).location
        ? (globalThis as { location: Location }).location.origin
        : 'http://localhost'
    const baseUrl = new URL(opts.apiBase.replace(/\/+$/, ''), origin)
    const qIdx = path.indexOf('?')
    const pathPart = qIdx >= 0 ? path.slice(0, qIdx) : path
    const pathQs = qIdx >= 0 ? path.slice(qIdx + 1) : ''
    const rel = pathPart.startsWith('/') ? pathPart : `/${pathPart}`
    baseUrl.pathname = `${baseUrl.pathname.replace(/\/$/, '')}${rel}`
    if (pathQs) new URLSearchParams(pathQs).forEach((v, k) => baseUrl.searchParams.set(k, v))
    if (opts.storefrontId && !baseUrl.searchParams.has('storefront')) {
      baseUrl.searchParams.set('storefront', opts.storefrontId)
    }
    return baseUrl.toString()
  }

  const buildHeaders = async (
    token: string,
    init?: RequestInit,
  ): Promise<Headers> => {
    const headers = new Headers(init?.headers)
    if (token && !headers.has('X-V2-Token')) headers.set('X-V2-Token', token)
    // Also send Authorization: Bearer for direct-to-core-v2 calls — the
    // relay reads X-V2-Token, core-v2 expects Authorization. Sending both
    // keeps the relay happy and unlocks direct core-v2 access (once CORS
    // is sorted on the cloud-run side).
    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    const accountId = opts.getAccountId ? await opts.getAccountId() : null
    if (accountId && !headers.has('X-Account-Id')) {
      headers.set('X-Account-Id', accountId)
    }
    if (init?.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json')
    }
    return headers
  }

  const now = (): number =>
    typeof performance !== 'undefined' ? performance.now() : Date.now()

  const wantsStream = (init?: RequestInit): boolean =>
    new Headers(init?.headers).get('Accept')?.includes('text/event-stream') ===
    true

  const snippet = (s: string): string =>
    s.length > MAX_LOGGED_BODY ? `${s.slice(0, MAX_LOGGED_BODY)}…` : s

  return async (path, init) => {
    const url = buildUrl(path)
    const method = (init?.method ?? 'GET').toUpperCase()
    const requestBody =
      typeof init?.body === 'string' ? snippet(init.body) : undefined
    const startedAt = now()
    const report = (status: number, ok: boolean, responseBody?: string) =>
      opts.onCall?.({
        method,
        url,
        status,
        ms: Math.round(now() - startedAt),
        ok,
        requestBody,
        responseBody,
      })
    try {
      let token = await opts.getToken()
      let response = await fetch(url, {
        ...init,
        headers: await buildHeaders(token, init),
      })
      if (response.status === 401 && opts.refreshToken) {
        token = await opts.refreshToken()
        response = await fetch(url, {
          ...init,
          headers: await buildHeaders(token, init),
        })
      }
      const responseBody =
        opts.onCall && !wantsStream(init)
          ? await response
              .clone()
              .text()
              .then(snippet)
              .catch(() => undefined)
          : undefined
      report(response.status, response.ok, responseBody)
      return response
    } catch (e) {
      report(0, false)
      throw e
    }
  }
}

// ── SSE stream parsing ────────────────────────────────────────────────
//
// Wire format (berrybot/app/stream.py:format_sse):  event: <type>\ndata: <json>\n\n
// We accept the formal SSE spec's trailing newlines and CRLF, but the
// server only emits LF + double-newline frame boundaries. Used by
// `streamUtterance` below.

const FRAME_BOUNDARY = /\r?\n\r?\n/

export function parseSseFrame(frame: string): SseEvent | null {
  let event: string | null = null
  const dataLines: string[] = []
  for (const raw of frame.split(/\r?\n/)) {
    if (!raw || raw.startsWith(':')) continue
    const idx = raw.indexOf(':')
    const field = idx === -1 ? raw : raw.slice(0, idx)
    const value = idx === -1 ? '' : raw.slice(idx + 1).replace(/^ /, '')
    if (field === 'event') event = value
    else if (field === 'data') dataLines.push(value)
  }
  if (!event) return null
  const dataStr = dataLines.join('\n')
  let data: Record<string, unknown> = {}
  if (dataStr.trim()) {
    try {
      data = JSON.parse(dataStr)
    } catch {
      data = { raw: dataStr }
    }
  }
  return { event: event as SseEventType, data }
}

// Stateful frame splitter: feed it chunks, get back complete events.
// Holds an incomplete frame across chunk boundaries until the next
// double-newline arrives.
export class SseParser {
  private buffer = ''

  feed(chunk: string): SseEvent[] {
    this.buffer += chunk
    const out: SseEvent[] = []
    while (true) {
      const m = this.buffer.match(FRAME_BOUNDARY)
      if (!m || m.index === undefined) break
      const frame = this.buffer.slice(0, m.index)
      this.buffer = this.buffer.slice(m.index + m[0].length)
      const ev = parseSseFrame(frame)
      if (ev) out.push(ev)
    }
    return out
  }

  flush(): SseEvent[] {
    if (!this.buffer.trim()) return []
    const ev = parseSseFrame(this.buffer)
    this.buffer = ''
    return ev ? [ev] : []
  }
}

// Read a fetch Response body as a stream of typed SSE events.
export async function* readSseStream(
  response: Response,
): AsyncGenerator<SseEvent> {
  if (!response.body) return
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  const parser = new SseParser()
  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      for (const ev of parser.feed(decoder.decode(value, { stream: true }))) {
        yield ev
      }
    }
    for (const ev of parser.flush()) yield ev
  } finally {
    reader.releaseLock()
  }
}

// ── Errors ────────────────────────────────────────────────────────────

// Thrown for any non-2xx relay response. `body` is the parsed JSON when
// the response had a JSON body (the relay's error envelopes look like
// `{ error, issues[] }` or `{ error, detail }`), otherwise the raw text.
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
    readonly path: string,
    // Response headers as a plain map — kept so failure telemetry can
    // pull correlation ids (x-request-id, x-vercel-id, cf-ray, …). Only
    // the small set used by `correlationHeaders()` is read; the map
    // is small even so.
    readonly headers: Record<string, string> = {},
  ) {
    super(
      `HTTP ${status} on ${path}: ${
        typeof body === 'string' ? body : JSON.stringify(body)
      }`,
    )
    this.name = 'ApiError'
  }
}

// Keys we lift into the send.failed payload for cross-service log
// correlation. Kept in the api module because that's where headers
// arrive; the telemetry site just calls this. Values are lower-cased
// header names (Response.headers is case-insensitive but iterates lower).
export const CORRELATION_HEADERS = [
  'x-request-id',
  'x-vercel-id',
  'x-vercel-cache',
  'cf-ray',
  'via',
  'x-served-by',
] as const

export function correlationHeaders(
  headers: Record<string, string> | undefined,
): Record<string, string> {
  if (!headers) return {}
  const out: Record<string, string> = {}
  for (const k of CORRELATION_HEADERS) {
    const v = headers[k]
    if (v) out[k] = v
  }
  return out
}

function parseBody(text: string): unknown {
  if (!text) return null
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

async function unwrap<T>(response: Response, path: string): Promise<T> {
  const body = parseBody(await response.text().catch(() => ''))
  if (!response.ok) {
    throw new ApiError(response.status, body, path, headersToObject(response))
  }
  return body as T
}

function headersToObject(response: Response): Record<string, string> {
  const out: Record<string, string> = {}
  response.headers.forEach((v, k) => { out[k.toLowerCase()] = v })
  return out
}

// Pull the meaningful string(s) out of one of the relay's error envelopes.
// They come in a few shapes — `{error, issues[]}` (submit preconditions),
// `{error, detail}` (geocode/zone fail), `{error: {error, detail}, ...}`
// (BerryBot error re-wrapped by the relay). Returns `error` + `detail` joined
// when both are present and distinct (the reason + the specifics).
function envelopeStrings(v: unknown, depth = 0): string[] {
  if (depth > 3 || !v || typeof v !== 'object') return []
  const b = v as Record<string, unknown>
  if (Array.isArray(b.issues) && b.issues.length) {
    // One issue per entry so per-issue rewrites in apiErrorMessage can
    // apply individually (Trello #144).
    return b.issues.map(String)
  }
  if (b.error && typeof b.error === 'object') {
    return envelopeStrings(b.error, depth + 1)
  }
  return [
    ...(typeof b.error === 'string' && b.error ? [b.error] : []),
    ...(typeof b.detail === 'string' && b.detail && b.detail !== b.error
      ? [b.detail]
      : []),
  ]
}

// Known /orders/submit precondition issues (raw backend text → customer-
// facing rewrite). The backend issues are developer-shaped (`(POST
// /conversations/{id}/contact)`) — fine for the log, jarring for the
// cart-side red banner that surfaces them verbatim. Trello #144 caught
// the contact-phone variant leaking; the other preconditions follow the
// same shape, so map them all.
const SUBMIT_ISSUE_REWRITES: ReadonlyArray<readonly [RegExp, string]> = [
  [
    /contact phone not on file/i,
    'We still need your phone number to confirm the order — please fill in the contact details.',
  ],
  [
    /fulfillment.*not set|no fulfillment/i,
    'Please choose pickup or delivery before submitting.',
  ],
  [
    /delivery address|address.*not (confirmed|set)/i,
    'Please confirm a delivery address before submitting.',
  ],
  [
    /cart.*(empty|no items)/i,
    'Your cart is empty — add an item before submitting.',
  ],
]

function friendlyIssue(raw: string): string {
  for (const [pat, friendly] of SUBMIT_ISSUE_REWRITES) {
    if (pat.test(raw)) return friendly
  }
  return raw
}

// A human-readable line from an error thrown by an api call. Unwraps an
// `ApiError`'s envelope (see above); falls back to the plain message otherwise.
export function apiErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    const parts = envelopeStrings(e.body).map(friendlyIssue)
    if (parts.length) return parts.join(' — ')
  }
  return e instanceof Error ? e.message : String(e)
}

// ── Endpoint wrappers ─────────────────────────────────────────────────

export interface BerryBotApi {
  startConversation(req: ConversationRequest): Promise<ConversationResponse>
  sendUtterance(
    conversationId: string,
    req: UtteranceRequest,
  ): Promise<UtteranceResponse>
  streamUtterance(
    conversationId: string,
    req: UtteranceRequest,
  ): AsyncGenerator<SseEvent>
  getState(conversationId: string): Promise<ConversationState>
  getMenu(menuId: string, storefrontId?: string): Promise<RawMenuItem[]>
  cartAdd(conversationId: string, req: CartAddRequest): Promise<unknown>
  cartRemove(conversationId: string, req: CartRemoveRequest): Promise<unknown>
  setFulfillment(
    conversationId: string,
    req: FulfillmentRequest,
  ): Promise<FulfillmentResponse>
  setContact(
    conversationId: string,
    req: ContactRequest,
  ): Promise<ContactResponse>
  addressAutocomplete(
    conversationId: string,
    query: string,
    sessionToken: string,
  ): Promise<AddressAutocompleteResponse>
  addressDetails(
    conversationId: string,
    placeId: string,
    sessionToken: string,
  ): Promise<PlaceDetailsResponse>
  randomAddress(
    conversationId: string,
    zone: 'in' | 'out',
  ): Promise<RandomAddressResponse>
  setSchedule(
    conversationId: string,
    timeIso: string,
  ): Promise<{ success: boolean; scheduled_for?: string | null }>
  submitOrder(conversationId: string): Promise<unknown>
  // Fire-and-forget UI telemetry. Lands in the conversation log so
  // modal/affordance/contact events surface at /log/#conv_<id> alongside
  // hints and turns. Failures are swallowed by the caller — never block
  // the UI on this.
  postClientEvent(
    conversationId: string,
    event: string,
    data?: Record<string, unknown> | null,
  ): Promise<void>
}

// Raw item shape from BerryCore's `GET /menus/{id}` envelope endpoint;
// the widget normalises this into bb-menu's MenuItem. `price` is the
// PriceSerializer shape ({fractional, formatted}); `item_image` is a
// full URL (envelope endpoint uses url_for, not the path-only form the
// older /menu_items endpoint returned). `categories` is synthesized
// client-side by getMenu() from the envelope's menu.categories[] →
// item_ids[] mapping — envelope items don't carry their own category
// list, but bb-menu expects one per item.
export interface RawModifierGroup {
  id?: string
  name?: string
  min_quantity?: number | null
  max_quantity?: number | null
  charge_above_quantity?: number | null
  repeatable?: boolean
  group_type?: string | null
  group_subtype?: string | null
  item_ids?: string[]
  // Resolved client-side by getMenu(): item_ids[] looked up against the
  // full menu.items[] (including modifier-type items), so consumers don't
  // have to rejoin the envelope themselves.
  option_names?: string[]
  // Per-option `modifier_type` ('addition' | 'removal' | 'choice'),
  // parallel to option_names. Resolved alongside the names from the
  // envelope's items[] modifier_type field. Used by bb-chat-modifier
  // to render the correct glyph default per option (Trello #136).
  option_types?: string[]
}

export interface RawMenuItem {
  id: string
  name?: string
  description?: string
  item_type?: string
  // BerryCore item-level type for modifier items: 'addition' | 'removal' | 'choice'.
  // Carried on modifier-type items only; the widget uses it to pick the
  // per-option glyph in bb-chat-modifier (Trello #136).
  modifier_type?: string
  price?: { fractional?: number | null; formatted?: string } | null
  categories?: Array<{ name?: string }>
  item_image?: string | null
  available?: boolean
  snoozed_until?: string | null
  // Per-item links into menu.modifier_groups[]. The envelope endpoint
  // returns only the IDs here; getMenu() also attaches the resolved
  // group bodies as `modifier_groups` below so downstream consumers
  // (and integration tests that inspect `min_quantity`) don't have to
  // rejoin against the envelope themselves.
  modifier_group_ids?: string[]
  modifier_groups?: RawModifierGroup[]
}

export function makeApi(fetch: BridgeFetch): BerryBotApi {
  const call = async <T>(path: string, init?: RequestInit): Promise<T> =>
    unwrap<T>(await fetch(path, init), path)
  const post = <T>(path: string, body?: unknown): Promise<T> =>
    call<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    })

  return {
    startConversation: (req) => post('/conversations', req),

    sendUtterance: (conversationId, req) =>
      post(`/conversations/${conversationId}/utterances`, req),

    async *streamUtterance(conversationId, req) {
      const path = `/conversations/${conversationId}/utterances`
      const response = await fetch(path, {
        method: 'POST',
        headers: { Accept: 'text/event-stream' },
        body: JSON.stringify(req),
      })
      if (!response.ok) {
        throw new ApiError(
          response.status,
          parseBody(await response.text().catch(() => '')),
          path,
          headersToObject(response),
        )
      }
      yield* readSseStream(response)
    },

    getState: (conversationId) =>
      call<ConversationState>(`/conversations/${conversationId}/state`),

    async getMenu(menuId, storefrontId) {
      // Envelope endpoint: returns {menu: {items, categories, modifier_groups, …}}.
      // storefront_id is what surfaces per-(storefront,item) snooze state as
      // `available` / `snoozed_until` on each item; without it the server
      // reports every item available. Modifier-type items live in items[]
      // too (the flat endpoint pre-filtered server-side), so we drop them
      // client-side. Categories are inverted: envelope categories[] carry
      // item_ids[], and we attach the first category name back onto each
      // item so RawMenuItem stays close to its old shape and bb-menu's
      // toMenuItem keeps working unchanged.
      const params = new URLSearchParams()
      if (storefrontId) params.set('storefront_id', storefrontId)
      const qs = params.toString()
      const raw = await call<{
        menu?: {
          items?: RawMenuItem[]
          categories?: Array<{ name?: string; item_ids?: string[] }>
          modifier_groups?: RawModifierGroup[]
        }
      }>(`/menus/${encodeURIComponent(menuId)}${qs ? `?${qs}` : ''}`)
      const rawItems = raw.menu?.items ?? []
      const categoryByItemId = new Map<string, string>()
      for (const cat of raw.menu?.categories ?? []) {
        const name = cat.name?.trim()
        if (!name) continue
        for (const id of cat.item_ids ?? []) {
          if (!categoryByItemId.has(id)) categoryByItemId.set(id, name)
        }
      }
      // Reorder items by walking categories[] outer × item_ids[] inner. The
      // envelope's categories[] is server-sorted by Category.position, and
      // each category's item_ids[] is sorted by CategoryMenuItem.position —
      // but items[] itself has no ORDER BY (see menus_controller#show), so
      // relying on items[] arrival order lets Postgres heap order leak into
      // the rendered section order. bb-menu groups by first-arrival, so
      // reordering here fixes the section order without a backend change.
      const itemById = new Map(rawItems.map((it) => [it.id, it] as const))
      const seenIds = new Set<string>()
      const items: RawMenuItem[] = []
      for (const cat of raw.menu?.categories ?? []) {
        for (const id of cat.item_ids ?? []) {
          const it = itemById.get(id)
          if (it && !seenIds.has(id)) {
            items.push(it)
            seenIds.add(id)
          }
        }
      }
      for (const it of rawItems) {
        if (!seenIds.has(it.id)) items.push(it)
      }
      // Resolve modifier-group `item_ids[]` against the FULL items list
      // (including modifier-type items) so consumers see option names, not
      // raw UUIDs. Modifier items get filtered out of the returned list
      // below; the name lookup happens before that filter.
      const nameById = new Map<string, string>()
      const typeById = new Map<string, string>()
      for (const it of items) {
        const n = (it.name ?? '').trim()
        if (it.id && n) nameById.set(it.id, n)
        if (it.id && it.modifier_type) typeById.set(it.id, it.modifier_type)
      }
      const groupsById = new Map<string, RawModifierGroup>()
      for (const g of raw.menu?.modifier_groups ?? []) {
        if (!g.id) continue
        const ids = (g.item_ids ?? []).filter((id) => nameById.has(id))
        groupsById.set(g.id, {
          ...g,
          option_names: ids.map((id) => nameById.get(id)!),
          option_types: ids.map((id) => typeById.get(id) ?? ''),
        })
      }
      return items
        .filter((it) => (it.item_type ?? 'standalone') === 'standalone')
        .map((it) => ({
          ...it,
          categories: categoryByItemId.has(it.id)
            ? [{ name: categoryByItemId.get(it.id)! }]
            : (it.categories ?? []),
          modifier_groups: (it.modifier_group_ids ?? [])
            .map((id) => groupsById.get(id))
            .filter((g): g is RawModifierGroup => g !== undefined),
        }))
    },

    cartAdd: (conversationId, req) =>
      post(`/conversations/${conversationId}/cart/add`, req),

    cartRemove: (conversationId, req) =>
      post(`/conversations/${conversationId}/cart/remove`, req),

    setFulfillment: (conversationId, req) =>
      post<FulfillmentResponse>(
        `/conversations/${conversationId}/fulfillment`,
        req,
      ),

    setContact: (conversationId, req) =>
      post<ContactResponse>(
        `/conversations/${conversationId}/contact`,
        req,
      ),

    addressAutocomplete: (conversationId, query, sessionToken) => {
      const qs = new URLSearchParams({
        q: query,
        session_token: sessionToken,
      })
      return call<AddressAutocompleteResponse>(
        `/conversations/${conversationId}/address-autocomplete?${qs}`,
      )
    },

    addressDetails: (conversationId, placeId, sessionToken) => {
      const qs = new URLSearchParams({
        place_id: placeId,
        session_token: sessionToken,
      })
      return call<PlaceDetailsResponse>(
        `/conversations/${conversationId}/address-details?${qs}`,
      )
    },

    randomAddress: (conversationId, zone) =>
      call<RandomAddressResponse>(
        `/conversations/${conversationId}/random-address?zone=${zone}`,
      ),

    setSchedule: (conversationId, timeIso) =>
      post(`/conversations/${conversationId}/schedule`, { time_iso: timeIso }),

    submitOrder: (conversationId) =>
      post(`/conversations/${conversationId}/orders/submit`),

    postClientEvent: async (conversationId, event, data) => {
      try {
        await fetch(`/conversations/${conversationId}/client_events`, {
          method: 'POST',
          body: JSON.stringify({ event, data: data ?? null }),
          // keepalive lets the request survive page-hide / unload —
          // matters for `send.failed` beacons fired when the browser
          // backgrounds the tab mid-fetch (the same condition that
          // aborted the utterance we're reporting).
          keepalive: true,
        })
      } catch {
        // Swallow — telemetry must never break the order flow.
      }
    },
  }
}
